// Article Detail Page - View a single KB article
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Button,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  ArrowBack,
  CalendarToday,
  Person,
  Visibility,
  ThumbUp,
  ThumbDown,
  LocalOffer,
} from '@mui/icons-material';
import { usePortal } from './PortalLayout';
import { articlesService } from '@/services/collections/articles';
import type { Article } from '@/types';
import ReactMarkdown from 'react-markdown';

export const ArticleDetailPage = () => {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const navigate = useNavigate();
  const { organization, project, branding } = usePortal();

  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<'helpful' | 'not-helpful' | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  // Fetch article by slug
  useEffect(() => {
    const fetchArticle = async () => {
      if (!organization || !project || !articleSlug) return;

      setLoading(true);
      try {
        // Get all published articles and find by slug
        const articles = await articlesService.getPublished(organization.id, project.id);
        const found = articles.find((a) => a.slug === articleSlug);

        if (found) {
          setArticle(found);
          // Increment view count
          articlesService.incrementViews(found.id).catch(console.error);
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [organization, project, articleSlug]);

  const handleFeedback = async (helpful: boolean) => {
    if (!article || feedbackSubmitted) return;

    setFeedback(helpful ? 'helpful' : 'not-helpful');
    setFeedbackSubmitted(true);

    try {
      await articlesService.markHelpful(article.id, helpful);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const formatDate = (timestamp: { toDate: () => Date } | undefined) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: branding.primaryColor }} />
      </Box>
    );
  }

  if (!article) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5" gutterBottom>
          Article Not Found
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          The article you're looking for doesn't exist or has been removed.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/p/${organization?.slug}/kb`)}
          sx={{ bgcolor: branding.primaryColor }}
        >
          Back to Knowledge Base
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to={`/p/${organization?.slug}`}
          underline="hover"
          color="inherit"
        >
          Home
        </Link>
        <Link
          component={RouterLink}
          to={`/p/${organization?.slug}/kb`}
          underline="hover"
          color="inherit"
        >
          Knowledge Base
        </Link>
        {article.category && (
          <Link
            component={RouterLink}
            to={`/p/${organization?.slug}/kb?category=${encodeURIComponent(article.category)}`}
            underline="hover"
            color="inherit"
          >
            {article.category}
          </Link>
        )}
        <Typography color="text.primary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {article.title}
        </Typography>
      </Breadcrumbs>

      {/* Back button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back
      </Button>

      {/* Article Content */}
      <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 2 }}>
        {/* Category & Tags */}
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {article.category && (
            <Chip
              label={article.category}
              size="small"
              sx={{
                bgcolor: alpha(branding.primaryColor, 0.1),
                color: branding.primaryColor,
                fontWeight: 'medium',
              }}
            />
          )}
          {article.tags?.map((tag) => (
            <Chip
              key={tag}
              icon={<LocalOffer sx={{ fontSize: 14 }} />}
              label={tag}
              size="small"
              variant="outlined"
              component={RouterLink}
              to={`/p/${organization?.slug}/kb?tag=${encodeURIComponent(tag)}`}
              clickable
              sx={{ fontSize: '0.75rem' }}
            />
          ))}
        </Stack>

        {/* Title */}
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          {article.title}
        </Typography>

        {/* Meta */}
        <Stack
          direction="row"
          spacing={3}
          sx={{ mb: 3, color: 'text.secondary', flexWrap: 'wrap', gap: 1 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person sx={{ fontSize: 18 }} />
            <Typography variant="body2">{article.authorName}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarToday sx={{ fontSize: 18 }} />
            <Typography variant="body2">
              {formatDate(article.publishedAt || article.updatedAt)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Visibility sx={{ fontSize: 18 }} />
            <Typography variant="body2">{article.viewCount || 0} views</Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Article Content - Markdown */}
        <Box
          sx={{
            '& h1': { fontSize: '1.75rem', fontWeight: 'bold', mt: 3, mb: 2 },
            '& h2': { fontSize: '1.5rem', fontWeight: 'bold', mt: 3, mb: 2 },
            '& h3': { fontSize: '1.25rem', fontWeight: 'bold', mt: 2, mb: 1.5 },
            '& p': { mb: 2, lineHeight: 1.7 },
            '& ul, & ol': { pl: 3, mb: 2 },
            '& li': { mb: 0.5 },
            '& code': {
              bgcolor: 'grey.100',
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: '0.875em',
              fontFamily: 'monospace',
            },
            '& pre': {
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 2,
              '& code': {
                bgcolor: 'transparent',
                p: 0,
                color: 'inherit',
              },
            },
            '& blockquote': {
              borderLeft: `4px solid ${branding.primaryColor}`,
              pl: 2,
              py: 1,
              ml: 0,
              bgcolor: alpha(branding.primaryColor, 0.05),
              borderRadius: '0 4px 4px 0',
            },
            '& a': {
              color: branding.primaryColor,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 1,
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              mb: 2,
            },
            '& th, & td': {
              border: '1px solid',
              borderColor: 'grey.300',
              p: 1,
              textAlign: 'left',
            },
            '& th': {
              bgcolor: 'grey.100',
              fontWeight: 'bold',
            },
          }}
        >
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Feedback Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            Was this article helpful?
          </Typography>
          {feedbackSubmitted ? (
            <Typography variant="body2" color="text.secondary">
              Thank you for your feedback!
            </Typography>
          ) : (
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
              <Button
                variant={feedback === 'helpful' ? 'contained' : 'outlined'}
                startIcon={<ThumbUp />}
                onClick={() => handleFeedback(true)}
                sx={{
                  borderColor: 'success.main',
                  color: feedback === 'helpful' ? 'white' : 'success.main',
                  bgcolor: feedback === 'helpful' ? 'success.main' : 'transparent',
                  '&:hover': {
                    bgcolor: feedback === 'helpful' ? 'success.dark' : alpha('#10b981', 0.1),
                    borderColor: 'success.main',
                  },
                }}
              >
                Yes ({(article.helpfulCount || 0)})
              </Button>
              <Button
                variant={feedback === 'not-helpful' ? 'contained' : 'outlined'}
                startIcon={<ThumbDown />}
                onClick={() => handleFeedback(false)}
                sx={{
                  borderColor: 'error.main',
                  color: feedback === 'not-helpful' ? 'white' : 'error.main',
                  bgcolor: feedback === 'not-helpful' ? 'error.main' : 'transparent',
                  '&:hover': {
                    bgcolor: feedback === 'not-helpful' ? 'error.dark' : alpha('#ef4444', 0.1),
                    borderColor: 'error.main',
                  },
                }}
              >
                No ({(article.notHelpfulCount || 0)})
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
